import React, { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  User,
  ArrowRight,
  Plane,
  Globe,
  Map,
  MapPin,
  Compass,
  Navigation,
  Camera,
  Palmtree,
  Heart,
  Shield,
  Clock,
  Phone,
  Send,
  ExternalLink,
  AtSign,
  Instagram,
  Linkedin,
  Youtube,
  Zap,
  Users,
  Briefcase,
  Utensils,
  LocateFixed,
  Sparkles,
  Check,
} from "lucide-react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import authApi from "../api/authApi";

const AVAILABLE_INTERESTS = [
  "Trekking",
  "Beaches",
  "Foodie",
  "Photography",
  "Nightlife",
  "Culture & Art",
  "Historical",
  "Adventure Sports",
  "Road Trips",
  "Solo Travel",
  "Wellness & Yoga",
  "Nature & Wildlife",
  "Backpacking",
  "Luxury Escapes",
];

const loginSchema = z.object({
  email: z.string().min(1, "Email is required").email("Invalid email address"),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

const signupSchema = z.object({
  name: z.string().min(2, "Full name is required (min 2 characters)"),
  email: z.string().min(1, "Email is required").email("Invalid email address"),
  password: z
    .string()
    .min(8, "Password must be at least 8 characters")
    .regex(/[A-Z]/, "Must contain at least 1 uppercase letter")
    .regex(/[a-z]/, "Must contain at least 1 lowercase letter")
    .regex(/[0-9]/, "Must contain at least 1 number"),
  age: z.coerce.number().min(13, "Age must be at least 13").max(120, "Please enter a valid age"),
  sex: z.string().min(1, "Please select your gender / sex"),
  profession: z.string().min(1, "Profession is required"),
  dietaryPreference: z.string().min(1, "Please select dietary preference"),
  location: z.string().min(1, "Location is required"),
});

export default function NewAuth({
  isSignup = false,
  setShowLoginDialog,
  setShowSignupDialog,
}) {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [mounted, setMounted] = useState(false);
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const [hoveredLink, setHoveredLink] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { login, signup, loginWithLinkedIn } = useAuth();
  const linkedInExchangeInProgressRef = useRef(false);

  // Handle LinkedIn OAuth 2.0 Redirect Callback
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const code = urlParams.get("code");
    const oauthError = urlParams.get("error");
    const errorDesc = urlParams.get("error_description");

    if (oauthError) {
      setError(errorDesc || "LinkedIn authentication encountered an error or was cancelled.");
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }

    if (code && !linkedInExchangeInProgressRef.current) {
      linkedInExchangeInProgressRef.current = true;
      window.history.replaceState({}, document.title, window.location.pathname);
      const redirectUri =
        process.env.REACT_APP_LINKEDIN_REDIRECT_URI || `${window.location.origin}/login`;
      setLoading(true);
      loginWithLinkedIn({ code, redirect_uri: redirectUri })
        .then(() => {
          navigate("/dashboard");
        })
        .catch((err) => {
          setError(
            err?.response?.data?.message ||
              "Failed to complete LinkedIn login. Please try again."
          );
        })
        .finally(() => {
          setLoading(false);
          linkedInExchangeInProgressRef.current = false;
        });
    }
  }, [location.search, loginWithLinkedIn, navigate]);

  const handleLinkedInLogin = () => {
    const clientId = process.env.REACT_APP_LINKEDIN_CLIENT_ID || "86ixg3errqwvln";
    const redirectUri =
      process.env.REACT_APP_LINKEDIN_REDIRECT_URI || `${window.location.origin}/login`;

    const state = Math.random().toString(36).substring(7);
    localStorage.setItem("linkedin_oauth_state", state);
    const scope = encodeURIComponent("openid profile email");
    const authUrl = `https://www.linkedin.com/oauth/v2/authorization?response_type=code&client_id=${clientId}&redirect_uri=${encodeURIComponent(
      redirectUri
    )}&state=${state}&scope=${scope}`;
    window.location.href = authUrl;
  };

  // Extended Profile State for Signup
  const [selectedInterests, setSelectedInterests] = useState(["Trekking", "Beaches", "Foodie"]);
  const [locationLoading, setLocationLoading] = useState(false);
  const [coordinates, setCoordinates] = useState(null);

  const [localIsSignup, setLocalIsSignup] = useState(isSignup);
  const showingSignup =
    typeof setShowLoginDialog === "undefined" &&
    typeof setShowSignupDialog === "undefined"
      ? localIsSignup
      : isSignup;

  const [resendCountdown, setResendCountdown] = useState(0);
  const [verificationPendingEmail, setVerificationPendingEmail] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState({ type: "", text: "" });

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    setLocalIsSignup(isSignup);
  }, [isSignup]);

  useEffect(() => {
    if (resendCountdown <= 0) return;
    const t = setInterval(() => setResendCountdown((c) => c - 1), 1000);
    return () => clearInterval(t);
  }, [resendCountdown]);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
    reset,
  } = useForm({
    resolver: zodResolver(showingSignup ? signupSchema : loginSchema),
  });

  const toggleInterest = (tag) => {
    setSelectedInterests((prev) =>
      prev.includes(tag) ? prev.filter((i) => i !== tag) : [...prev, tag]
    );
  };

  const handleGetCurrentLocation = () => {
    if (!navigator.geolocation) {
      setError("Geolocation is not supported by your browser.");
      return;
    }
    setLocationLoading(true);
    setError("");
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        setCoordinates([lng, lat]);
        try {
          const res = await axios.get(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
          );
          const city =
            res.data?.address?.city ||
            res.data?.address?.town ||
            res.data?.address?.village ||
            res.data?.address?.state ||
            res.data?.display_name?.split(",")[0] ||
            "Current Location";
          setValue("location", city, { shouldValidate: true });
        } catch (e) {
          setValue("location", `${lat.toFixed(2)}, ${lng.toFixed(2)}`, { shouldValidate: true });
        } finally {
          setLocationLoading(false);
        }
      },
      (err) => {
        console.warn("Geolocation error:", err.message);
        setLocationLoading(false);
        setError("Could not automatically retrieve location. Please enter city manually.");
      },
      { timeout: 10000 }
    );
  };

  const onSubmit = async (data) => {
    setError("");
    setLoading(true);

    try {
      if (showingSignup) {
        if (selectedInterests.length === 0) {
          setError("Please select at least one travel interest.");
          setLoading(false);
          return;
        }

        const payload = {
          name: data.name.trim(),
          email: data.email.toLowerCase().trim(),
          password: data.password,
          age: Number(data.age),
          gender: data.sex,
          sex: data.sex,
          profession: data.profession?.trim(),
          dietaryPreference: data.dietaryPreference,
          location: data.location?.trim(),
          coordinates: coordinates || [],
          interests: selectedInterests,
        };

        // Call the new NestJS backend via AuthContext
        await signup(payload);

        setVerificationPendingEmail(data.email);
        setResendMessage({ type: "", text: "" });
      } else {
        // Call the new NestJS login via AuthContext
        await login({
          email: data.email.toLowerCase().trim(),
          password: data.password,
        });

        if (setShowLoginDialog) setShowLoginDialog(false);
        if (setShowSignupDialog) setShowSignupDialog(false);

        const redirectParam = new URLSearchParams(location.search).get("redirect");
        let sessionRedirect = null;
        try {
          sessionRedirect = sessionStorage.getItem("sync_redirect");
        } catch (e) {}
        const fromPath = location.state?.from
          ? (typeof location.state.from === "string"
              ? location.state.from
              : location.state.from.pathname + (location.state.from.search || ""))
          : null;
        const targetUrl = redirectParam || sessionRedirect || fromPath || "/dashboard";
        try {
          sessionStorage.removeItem("sync_redirect");
        } catch (e) {}
        navigate(targetUrl, { replace: true });
      }
    } catch (err) {
      console.error("Auth error:", err);
      const resData = err?.response?.data;
      const statusCode = err?.response?.status;
      const message =
        resData?.message ||
        resData?.error ||
        err?.message ||
        "Authentication failed. Please check your credentials.";

      const formattedMsg = Array.isArray(message) ? message.join(", ") : message;

      if (
        statusCode === 403 ||
        (statusCode === 401 && formattedMsg.toLowerCase().includes("verify")) ||
        resData?.emailVerified === false
      ) {
        setVerificationPendingEmail(resData?.email || data.email);
        setResendMessage({ type: "", text: "" });
      } else {
        setError(formattedMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (resendCountdown > 0) return;
    setResendLoading(true);
    setResendMessage({ type: "", text: "" });
    try {
      // In the new backend, signup / forgot-password can be triggered or resend if configured
      await authApi.forgotPassword({ email: verificationPendingEmail });
      setResendMessage({
        type: "success",
        text: "A new verification / access link has been sent to your email address.",
      });
      setResendCountdown(60);
    } catch (err) {
      const msg =
        err?.response?.data?.message || err?.message || "Failed to resend verification email.";
      setResendMessage({
        type: "error",
        text: Array.isArray(msg) ? msg.join(", ") : msg,
      });
    } finally {
      setResendLoading(false);
    }
  };

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (newsletterEmail) {
      setSubscribed(true);
      setTimeout(() => {
        setSubscribed(false);
        setNewsletterEmail("");
      }, 3000);
    }
  };

  const footerSections = [
    {
      title: "Product",
      links: [
        { name: "Features", href: "/#features", icon: <Zap size={14} /> },
        { name: "How It Works", href: "/#working", icon: <Globe size={14} /> },
        { name: "Pricing", href: "/#pricing", icon: <Heart size={14} /> },
        { name: "Integrations", href: "/#integrations", icon: <Users size={14} /> },
      ],
    },
    {
      title: "Support",
      links: [
        { name: "Help Center", href: "/contact" },
        { name: "Contact Us", href: "/contact" },
        { name: "Community", href: "/#community" },
      ],
    },
    {
      title: "Company",
      links: [
        { name: "About Us", href: "/#about" },
        { name: "Careers", href: "/careers" },
        { name: "Blog", href: "/#blog" },
        { name: "Press Kit", href: "/#press" },
      ],
    },
    {
      title: "Legal",
      links: [
        { name: "Privacy Policy", href: "/privacy-policy" },
        { name: "Terms of Service", href: "/terms-and-services" },
        { name: "Cookie Policy", href: "/terms-and-services#cookies" },
        { name: "GDPR", href: "/terms-and-services#gdpr" },
      ],
    },
  ];

  const socialLinks = [
    { icon: <AtSign size={20} />, label: "Threads", href: "#", color: "hover:text-gray-300", bgHover: "hover:bg-gray-700" },
    { icon: <svg viewBox="0 0 24 24" fill="currentColor" width={20} height={20}><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.6l-5.165-6.75-5.913 6.75h-3.308l7.73-8.835L.424 2.25h6.74l4.888 6.469L17.244 2.25h.75zm-1.161 17.52h1.833L7.084 4.126H5.117l12.926 15.644z"/></svg>, label: "X (Twitter)", href: "#", color: "hover:text-white", bgHover: "hover:bg-gray-600" },
    { icon: <Instagram size={20} />, label: "Instagram", href: "https://www.instagram.com/syncandexplore?igsh=MXV5OXgzdndoMjlwOQ==", color: "hover:text-pink-400", bgHover: "hover:bg-pink-900/30" },
    { icon: <Linkedin size={20} />, label: "LinkedIn", href: "https://www.linkedin.com/company/sync-explore/posts/?feedView=all", color: "hover:text-blue-400", bgHover: "hover:bg-blue-900/30" },
    { icon: <Youtube size={20} />, label: "YouTube", href: "#", color: "hover:text-red-400", bgHover: "hover:bg-red-900/30" },
  ];

  const contactInfo = [
    { icon: <MapPin size={16} />, text: "Hno-337g/f Kh No. 126, Saini Mohalla, Nangloi - Iii, West Delhi, New Delhi, Delhi, India, 110041" },
    { icon: <Phone size={16} />, text: "+91 6391433858" },
    { icon: <Mail size={16} />, text: "contact@syncandexplore.com" },
  ];

  return (
    <div className="min-h-screen w-full flex flex-col bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50 relative overflow-hidden font-sans">
      {/* Background Animated Blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-cyan-400 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob"></div>
        <div className="absolute top-40 right-10 w-72 h-72 bg-blue-400 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-8 left-20 w-72 h-72 bg-teal-400 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-4000"></div>
      </div>

      {/* Floating Travel Icons */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <Plane
          className="floating-icon absolute top-[10%] left-[5%] w-8 h-8 md:w-12 md:h-12 text-teal-400 opacity-30"
          style={{ animationDelay: "0s" }}
        />
        <Globe
          className="floating-icon absolute top-[20%] right-[10%] w-10 h-10 md:w-14 md:h-14 text-cyan-400 opacity-40"
          style={{ animationDelay: "1s" }}
        />
        <Map
          className="floating-icon absolute top-[70%] left-[8%] w-7 h-7 md:w-10 md:h-10 text-blue-400 opacity-35"
          style={{ animationDelay: "2s" }}
        />
        <MapPin
          className="floating-icon absolute top-[40%] right-[20%] w-6 h-6 md:w-9 md:h-9 text-teal-500 opacity-30"
          style={{ animationDelay: "1.5s" }}
        />
        <Compass
          className="floating-icon absolute bottom-[15%] right-[25%] w-9 h-9 md:w-12 md:h-12 text-cyan-500 opacity-35"
          style={{ animationDelay: "0.5s" }}
        />
        <Navigation
          className="floating-icon absolute top-[50%] left-[3%] w-8 h-8 md:w-11 md:h-11 text-blue-500 opacity-30"
          style={{ animationDelay: "2.5s" }}
        />
        <Camera
          className="floating-icon absolute bottom-[50%] left-[30%] w-7 h-7 md:w-10 md:h-10 text-teal-400 opacity-40"
          style={{ animationDelay: "1.2s" }}
        />
        <Palmtree
          className="floating-icon absolute top-[30%] left-[15%] w-8 h-8 md:w-11 md:h-11 text-cyan-400 opacity-30"
          style={{ animationDelay: "3s" }}
        />
        <Globe
          className="floating-icon absolute bottom-[10%] left-[20%] w-9 h-9 md:w-13 md:h-13 text-blue-400 opacity-35"
          style={{ animationDelay: "2.8s" }}
        />
        <Plane
          className="floating-icon absolute bottom-[30%] right-[8%] w-6 h-6 md:w-9 md:h-9 text-teal-500 opacity-30 rotate-45"
          style={{ animationDelay: "1.8s" }}
        />
      </div>

      <style>{`
        @keyframes blob {
          0%, 100% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>

      {/* Form Section - Centered */}
      <div className="flex-1 flex items-center justify-center p-4 mt-10 md:mt-12 relative z-10">
        {/* Main Card */}
        <div
          className={`w-full ${
            showingSignup ? "max-w-2xl" : "max-w-md"
          } relative bg-white rounded-3xl shadow-2xl overflow-hidden transition-all duration-500 ${
            mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
        >
          {/* Header Section with Gradient */}
          <div className="relative bg-gradient-to-br from-[#1A8F98] to-[#0b959c] p-6 sm:p-8 pb-10 sm:pb-12">
            {/* Decorative Plane Icon */}
            <div className="absolute top-4 right-4 opacity-20">
              <Plane className="w-16 h-16 text-white transform rotate-45" />
            </div>

            <div
              className={`text-center relative z-10 transition-all duration-700 delay-200 ${
                mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"
              }`}
            >
              <h1 className="text-2xl sm:text-3xl font-bold text-white mb-1.5">
                Sync and Explore
              </h1>
              <p className="text-xs sm:text-sm text-cyan-100">
                {showingSignup ? "Create Your Traveler Profile & Account" : "Welcome Back"}
              </p>
            </div>

            {/* Wave Effect */}
            <div className="absolute -bottom-1.5 left-0 right-0">
              <svg viewBox="0 0 1440 120" className="w-full h-10 sm:h-12">
                <path
                  fill="#ffffff"
                  d="M0,64L80,69.3C160,75,320,85,480,80C640,75,800,53,960,48C1120,43,1280,53,1360,58.7L1440,64L1440,120L1360,120C1280,120,1120,120,960,120C800,120,640,120,480,120C320,120,160,120,80,120L0,120Z"
                ></path>
              </svg>
            </div>
          </div>

          {/* Form Content */}
          <div className="p-6 sm:p-8 pt-3 sm:pt-4">
            {verificationPendingEmail ? (
              <div className="space-y-6 text-center py-4">
                <div className="relative w-20 h-20 mx-auto mb-6">
                  <div className="absolute inset-0 rounded-full bg-teal-500/10 border border-teal-500/20 animate-ping"></div>
                  <div className="relative w-20 h-20 bg-teal-500/10 border border-teal-400/20 rounded-full flex items-center justify-center text-[#1A8F98] mx-auto">
                    <Mail className="w-10 h-10" />
                  </div>
                </div>

                <div className="space-y-2">
                  <h2 className="text-2xl font-bold text-gray-800 tracking-tight">
                    Verify your email address
                  </h2>
                  <p className="text-sm text-gray-500 max-w-sm mx-auto leading-relaxed">
                    We've sent a secure verification link to{" "}
                    <span className="font-semibold text-gray-800">
                      {verificationPendingEmail}
                    </span>.
                  </p>
                  <p className="text-xs text-gray-400 max-w-xs mx-auto">
                    Please click the link in that email to fully activate your account.
                  </p>
                </div>

                {resendMessage.text && (
                  <div
                    className={`text-xs p-3.5 rounded-xl border font-medium ${
                      resendMessage.type === "success"
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : "bg-red-50 text-red-700 border-red-200"
                    }`}
                  >
                    {resendMessage.text}
                  </div>
                )}

                <div className="space-y-3 pt-2">
                  <button
                    type="button"
                    onClick={handleResendVerification}
                    disabled={resendLoading || resendCountdown > 0}
                    className="w-full bg-gradient-to-r from-[#1A8F98] to-[#0b959c] text-white font-semibold py-3.5 rounded-xl hover:shadow-lg hover:scale-[1.02] active:scale-95 transition-all duration-300 disabled:opacity-60 disabled:scale-100 flex items-center justify-center gap-2"
                  >
                    {resendLoading ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    ) : resendCountdown > 0 ? (
                      `Resend link in ${resendCountdown}s`
                    ) : (
                      <>
                        <span>Resend Verification Email</span>
                        <Send className="w-4 h-4" />
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setVerificationPendingEmail("");
                      setResendMessage({ type: "", text: "" });
                      setError("");
                    }}
                    className="w-full bg-white border-2 border-gray-200 text-gray-700 font-semibold py-3.5 rounded-xl hover:bg-gray-50 hover:border-gray-300 hover:shadow-sm transition-all duration-300 flex items-center justify-center gap-2"
                  >
                    Back to Login
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Account Credentials Section */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                  {/* Name Field (Signup Only) */}
                  {showingSignup && (
                    <div className="sm:col-span-2">
                      <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">
                        Full Name <span className="text-red-500">*</span>
                      </label>
                      <div className="relative group">
                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 group-hover:text-[#1A8F98] transition-colors" />
                        <input
                          type="text"
                          {...register("name")}
                          placeholder="John Doe"
                          className="w-full pl-9 pr-4 py-2.5 text-xs sm:text-sm border-2 border-gray-200 rounded-xl focus:border-[#1A8F98] focus:ring-2 focus:ring-[#1A8F98]/20 transition-all duration-300 outline-none hover:border-gray-300"
                        />
                      </div>
                      {errors.name && (
                        <p className="text-red-500 text-xs mt-1">{errors.name.message}</p>
                      )}
                    </div>
                  )}

                  {/* Email */}
                  <div className={showingSignup ? "sm:col-span-1" : "sm:col-span-2"}>
                    <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <div className="relative group">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 group-hover:text-[#1A8F98] transition-colors" />
                      <input
                        type="email"
                        {...register("email")}
                        placeholder="user@example.com"
                        className="w-full pl-9 pr-4 py-2.5 text-xs sm:text-sm border-2 border-gray-200 rounded-xl focus:border-[#1A8F98] focus:ring-2 focus:ring-[#1A8F98]/20 transition-all duration-300 outline-none hover:border-gray-300"
                      />
                    </div>
                    {errors.email && (
                      <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>
                    )}
                  </div>

                  {/* Password */}
                  <div className={showingSignup ? "sm:col-span-1" : "sm:col-span-2"}>
                    <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">
                      Password <span className="text-red-500">*</span>
                    </label>
                    <div className="relative group">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 group-hover:text-[#1A8F98] transition-colors" />
                      <input
                        type={showPassword ? "text" : "password"}
                        {...register("password")}
                        placeholder={showingSignup ? "Min 8 chars, 1 upper & 1 number" : "Your password"}
                        className="w-full pl-9 pr-10 py-2.5 text-xs sm:text-sm border-2 border-gray-200 rounded-xl focus:border-[#1A8F98] focus:ring-2 focus:ring-[#1A8F98]/20 transition-all duration-300 outline-none hover:border-gray-300 [&::-ms-reveal]:hidden [&::-ms-clear]:hidden"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-[#1A8F98] transition-colors"
                      >
                        {showPassword ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                    {errors.password && (
                      <p className="text-red-500 text-xs mt-1">{errors.password.message}</p>
                    )}
                  </div>
                </div>

                {/* Extended Profile Fields (Signup Only) */}
                {showingSignup && (
                  <div className="space-y-3.5 pt-1 border-t border-gray-100">
                    {/* Age & Sex */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                      <div>
                        <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">
                          Age <span className="text-red-500">*</span>
                        </label>
                        <div className="relative group">
                          <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 group-hover:text-[#1A8F98] transition-colors" />
                          <input
                            type="number"
                            min="13"
                            max="120"
                            {...register("age")}
                            placeholder="e.g. 24"
                            className="w-full pl-9 pr-4 py-2.5 text-xs sm:text-sm border-2 border-gray-200 rounded-xl focus:border-[#1A8F98] focus:ring-2 focus:ring-[#1A8F98]/20 transition-all duration-300 outline-none hover:border-gray-300"
                          />
                        </div>
                        {errors.age && (
                          <p className="text-red-500 text-xs mt-1">{errors.age.message}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">
                          Sex / Gender <span className="text-red-500">*</span>
                        </label>
                        <select
                          {...register("sex")}
                          defaultValue=""
                          className="w-full px-3.5 py-2.5 text-xs sm:text-sm border-2 border-gray-200 rounded-xl focus:border-[#1A8F98] focus:ring-2 focus:ring-[#1A8F98]/20 transition-all duration-300 outline-none hover:border-gray-300 bg-white"
                        >
                          <option value="" disabled>
                            Select Gender / Sex
                          </option>
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                          <option value="Non-binary">Non-binary</option>
                          <option value="Other">Other</option>
                          <option value="Prefer not to say">Prefer not to say</option>
                        </select>
                        {errors.sex && (
                          <p className="text-red-500 text-xs mt-1">{errors.sex.message}</p>
                        )}
                      </div>
                    </div>

                    {/* Profession & Dietary Preference */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                      <div>
                        <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">
                          Profession <span className="text-red-500">*</span>
                        </label>
                        <div className="relative group">
                          <Briefcase className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 group-hover:text-[#1A8F98] transition-colors" />
                          <input
                            type="text"
                            {...register("profession")}
                            placeholder="e.g. Software Engineer"
                            className="w-full pl-9 pr-4 py-2.5 text-xs sm:text-sm border-2 border-gray-200 rounded-xl focus:border-[#1A8F98] focus:ring-2 focus:ring-[#1A8F98]/20 transition-all duration-300 outline-none hover:border-gray-300"
                          />
                        </div>
                        {errors.profession && (
                          <p className="text-red-500 text-xs mt-1">
                            {errors.profession.message}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">
                          Dietary Preference <span className="text-red-500">*</span>
                        </label>
                        <div className="relative group">
                          <Utensils className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none group-hover:text-[#1A8F98] transition-colors" />
                          <select
                            {...register("dietaryPreference")}
                            defaultValue=""
                            className="w-full pl-9 pr-4 py-2.5 text-xs sm:text-sm border-2 border-gray-200 rounded-xl focus:border-[#1A8F98] focus:ring-2 focus:ring-[#1A8F98]/20 transition-all duration-300 outline-none hover:border-gray-300 bg-white"
                          >
                            <option value="" disabled>
                              Select Dietary Preference
                            </option>
                            <option value="Any">Any / No Restrictions</option>
                            <option value="Vegetarian">Vegetarian</option>
                            <option value="Non-Vegetarian">Non-Vegetarian</option>
                            <option value="Vegan">Vegan</option>
                            <option value="Jain">Jain</option>
                            <option value="Eggetarian">Eggetarian</option>
                            <option value="Halal">Halal</option>
                            <option value="Kosher">Kosher</option>
                          </select>
                        </div>
                        {errors.dietaryPreference && (
                          <p className="text-red-500 text-xs mt-1">
                            {errors.dietaryPreference.message}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Location with Auto GPS detector */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="block text-xs sm:text-sm font-semibold text-gray-700">
                          Location / City <span className="text-red-500">*</span>
                        </label>
                        <button
                          type="button"
                          onClick={handleGetCurrentLocation}
                          disabled={locationLoading}
                          className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#1A8F98] hover:text-[#0b959c] transition-colors disabled:opacity-50"
                        >
                          <LocateFixed
                            className={`w-3.5 h-3.5 ${
                              locationLoading ? "animate-spin" : ""
                            }`}
                          />
                          <span>
                            {locationLoading
                              ? "Detecting GPS..."
                              : "📍 Use Current Location"}
                          </span>
                        </button>
                      </div>
                      <div className="relative group">
                        <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 group-hover:text-[#1A8F98] transition-colors" />
                        <input
                          type="text"
                          {...register("location")}
                          placeholder="e.g. New Delhi, India"
                          className="w-full pl-9 pr-4 py-2.5 text-xs sm:text-sm border-2 border-gray-200 rounded-xl focus:border-[#1A8F98] focus:ring-2 focus:ring-[#1A8F98]/20 transition-all duration-300 outline-none hover:border-gray-300"
                        />
                      </div>
                      {errors.location && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.location.message}
                        </p>
                      )}
                    </div>

                    {/* Travel Interests Tag Selector */}
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <label className="block text-xs sm:text-sm font-semibold text-gray-700">
                          Travel Interests <span className="text-red-500">*</span>
                        </label>
                        <span className="text-[11px] font-medium text-gray-400">
                          {selectedInterests.length} selected
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto p-2 bg-gray-50/80 rounded-xl border border-gray-200">
                        {AVAILABLE_INTERESTS.map((tag) => {
                          const isSelected = selectedInterests.includes(tag);
                          return (
                            <button
                              key={tag}
                              type="button"
                              onClick={() => toggleInterest(tag)}
                              className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium transition-all ${
                                isSelected
                                  ? "bg-gradient-to-r from-[#1A8F98] to-[#0b959c] text-white shadow-sm scale-[1.02]"
                                  : "bg-white text-gray-600 border border-gray-200 hover:border-gray-300 hover:bg-gray-100"
                              }`}
                            >
                              {isSelected && <Check className="w-3 h-3" />}
                              <span>{tag}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}

                {/* Forgot Password */}
                {!showingSignup && (
                  <div className="text-right">
                    <button
                      type="button"
                      onClick={() => navigate("/forgot-password")}
                      className="text-xs sm:text-sm text-[#1A8F98] hover:text-[#0b959c] font-medium transition-colors"
                    >
                      Forgot Password?
                    </button>
                  </div>
                )}

                {/* Error Message */}
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm">
                    {error}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <button
                    onClick={handleSubmit(onSubmit)}
                    disabled={isSubmitting || loading}
                    className="flex-1 bg-gradient-to-r from-[#1A8F98] to-[#0b959c] text-white font-semibold py-3 rounded-xl hover:shadow-lg hover:scale-[1.02] active:scale-95 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {loading || isSubmitting ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <>
                        <span>{showingSignup ? "Sign Up" : "Login"}</span>
                        <ArrowRight className="w-5 h-5" />
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={() => navigate("/admin-login")}
                    className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-gray-900 font-semibold px-6 py-3 rounded-xl hover:shadow-lg hover:scale-[1.02] active:scale-95 transition-all duration-300"
                  >
                    Admin
                  </button>
                </div>

                {/* Divider */}
                <div className="flex items-center gap-4 my-6">
                  <div className="flex-1 h-px bg-gray-200"></div>
                  <span className="text-sm text-gray-500">or continue with</span>
                  <div className="flex-1 h-px bg-gray-200"></div>
                </div>

                {/* Social Logins */}
                <div className="space-y-3">
                  <button
                    onClick={handleLinkedInLogin}
                    disabled={loading}
                    type="button"
                    className="w-full bg-[#0A66C2] text-white font-semibold py-3 rounded-xl hover:bg-[#084e96] hover:shadow-md transition-all duration-300 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                  >
                    <svg className="w-5 h-5 fill-current text-white" viewBox="0 0 24 24">
                      <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.28 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.75M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z" />
                    </svg>
                    {loading ? "Connecting to LinkedIn..." : "Continue with LinkedIn"}
                  </button>
                </div>

                {/* Toggle Login/Signup */}
                <div className="text-center pt-4">
                  <span className="text-gray-600">
                    {showingSignup
                      ? "Already have an account?"
                      : "Don't have an account?"}{" "}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      if (
                        typeof setShowLoginDialog !== "undefined" ||
                        typeof setShowSignupDialog !== "undefined"
                      ) {
                        if (isSignup) {
                          setShowSignupDialog?.(false);
                          setShowLoginDialog?.(true);
                        } else {
                          setShowLoginDialog?.(false);
                          setShowSignupDialog?.(true);
                        }
                      } else {
                        setLocalIsSignup((s) => !s);
                      }
                    }}
                    className="text-[#1A8F98] hover:text-[#0b959c] font-semibold transition-colors"
                  >
                    {showingSignup ? "Login" : "Sign Up"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="relative w-full bg-gradient-to-br from-[#1A8F98] to-[#0b959c] text-white mt-auto">
        <div className="absolute top-0 left-0 right-0 h-1 px-6 py-12 footer-content"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 mb-12">
            {/* Brand Section */}
            <div className="lg:col-span-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="sync flex justify-center md:justify-start">
                  <img
                    src="/images/sync_logo.png"
                    className="footer-logo w-16 h-auto ml-0 md:ml-6 z-10 block object-contain relative hover:scale-105 transition-transform duration-300 cursor-pointer"
                    alt="Sync and Explore Logo"
                  />
                </div>
                <h2 className="text-2xl font-bold text-white">Sync and Explore</h2>
              </div>
              <p className="text-gray-100 mb-6 leading-relaxed">
                We Sync, AI Plans. You Explore. Your intelligent travel companion for seamless adventures around the world.
              </p>

              {/* Contact Info */}
              <div className="space-y-3 mb-6">
                {contactInfo.map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3 text-sm group">
                    <span className="text-blue-400 mt-0.5 group-hover:scale-110 transition-transform">
                      {item.icon}
                    </span>
                    <span className="text-gray-300 group-hover:text-gray-300 transition-colors">
                      {item.text}
                    </span>
                  </div>
                ))}
              </div>

              {/* Social Links */}
              <div className="flex gap-3 mt-4">
                {socialLinks.map((social, idx) => (
                  <a
                    key={idx}
                    href={social.href}
                    aria-label={social.label}
                    title={social.label}
                    className={`group relative w-12 h-12 bg-gradient-to-br from-slate-700 to-slate-800 rounded-xl flex items-center justify-center ${social.color} ${social.bgHover} transition-all duration-300 hover:scale-125 hover:shadow-2xl hover:-translate-y-2 border border-slate-600 hover:border-slate-400 cursor-pointer`}
                  >
                    <span className="relative z-10 flex items-center justify-center">
                      {social.icon}
                    </span>
                    <span className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                  </a>
                ))}
              </div>
            </div>

            {/* Links Sections */}
            <div className="lg:col-span-5 grid grid-cols-2 sm:grid-cols-4 gap-8 sm:gap-6">
              {footerSections.map((section, idx) => (
                <div key={idx}>
                  <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">
                    {section.title}
                  </h3>
                  <ul className="space-y-2.5">
                    {section.links.map((link, linkIdx) => (
                      <li key={linkIdx}>
                        <a
                          href={link.href}
                          onMouseEnter={() => setHoveredLink(`${idx}-${linkIdx}`)}
                          onMouseLeave={() => setHoveredLink(null)}
                          className="text-sm text-gray-300 hover:text-white transition-colors duration-200 flex items-center gap-1.5 group"
                        >
                          {link.icon && (
                            <span className="text-blue-400 opacity-0 group-hover:opacity-100 transition-opacity">
                              {link.icon}
                            </span>
                          )}
                          <span
                            className={`${
                              hoveredLink === `${idx}-${linkIdx}`
                                ? "translate-x-1"
                                : ""
                            } transition-transform duration-200`}
                          >
                            {link.name}
                          </span>
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {/* Newsletter Section */}
            <div className="lg:col-span-3">
              <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">
                Stay Updated
              </h3>
              <p className="text-sm text-gray-300 mb-4">
                Get travel tips, exclusive deals, and AI-powered recommendations.
              </p>
              <form onSubmit={handleSubscribe} className="space-y-3">
                <div className="relative">
                  <input
                    type="email"
                    value={newsletterEmail}
                    onChange={(e) => setNewsletterEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 pr-12 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
                  />
                  <Mail
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500"
                    size={18}
                  />
                </div>
                <button
                  type="submit"
                  className="newsletter-button w-full py-3 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center gap-2 border-2 bg-white border-[#1A8F98] hover:shadow-lg hover:scale-105"
                >
                  <span className="bg-gradient-to-b from-[#1A8F98] to-[#0b959c] bg-clip-text text-transparent font-semibold">
                    {subscribed ? "Subscribed!" : "Subscribe"}
                  </span>
                  <Send
                    size={16}
                    className="text-transparent bg-gradient-to-b from-[#1A8F98] to-[#0b959c] bg-clip-text"
                  />
                </button>
              </form>

              {/* Trust Badges */}
              <div className="mt-6 flex items-center gap-4 text-xs text-gray-300">
                <div className="flex items-center gap-1">
                  <Shield size={14} className="text-green-300" />
                  <span>Secure</span>
                </div>
                <div className="flex items-center gap-1">
                  <Clock size={14} className="text-blue-300" />
                  <span>24/7 Support</span>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-slate-800 pt-8">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4 text-sm">
              <p className="text-gray-100 text-center sm:text-left">
                © {new Date().getFullYear()} Sync and Explore. All rights reserved. Made with{" "}
                <Heart size={14} className="inline text-red-500 animate-pulse" /> for travelers.
              </p>
              <div className="flex flex-wrap justify-center gap-4 sm:gap-6">
                <a
                  href="#sitemap"
                  className="text-gray-300 hover:text-white transition-colors flex items-center gap-1"
                >
                  Sitemap <ExternalLink size={12} />
                </a>
                <a
                  href="#accessibility"
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  Accessibility
                </a>
                <a
                  href="/terms-and-services#cookies"
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  Cookie Settings
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Decorative Background Elements */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-20">
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl"></div>
        </div>
      </footer>
    </div>
  );
}
